import { useState, useEffect } from 'react';
import { useBooking } from '../context/BookingContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from '../components/Modal';

const BookingSystem = () => {
  const { courts, bookings, settings, addBooking, isSlotOccupied, currentUser, setCurrentUser, refreshBookings } = useBooking();
  const navigate = useNavigate();
  const location = useLocation(); // <-- added
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [userName, setUserName] = useState(currentUser);
  const [selectedCells, setSelectedCells] = useState<{ courtId: number; timeIndex: number }[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [photoBase64, setPhotoBase64] = useState('');

  // ===== Read selected date from navigation state =====
  useEffect(() => {
    const state = location.state as { selectedDate?: string } | null;
    if (state?.selectedDate) {
      setSelectedDate(state.selectedDate);
      // Clear any previous selections when date changes
      setSelectedCells([]);
    }
  }, [location.state]);

  // ===== Update userName when context changes =====
  useEffect(() => {
    setUserName(currentUser);
  }, [currentUser]);

  const generateTimeSlots = (startTime: string, endTime: string, slotDuration: number) => {
    const slots: string[] = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const totalStart = startHour * 60 + startMin;
    const totalEnd = endHour * 60 + endMin;
    for (let t = totalStart; t < totalEnd; t += slotDuration) {
      const h = Math.floor(t / 60);
      const m = t % 60;
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
    return slots;
  };

  if (!settings) {
    return <div className="flex justify-center items-center h-64 text-gray-500">Loading configuration...</div>;
  }

  const TIME_SLOTS = generateTimeSlots(
    settings.startTime,
    settings.endTime,
    parseInt(settings.slotDuration)
  );

  const getSlotEndTime = (index: number) => {
    return TIME_SLOTS[index + 1] || `${(parseInt(TIME_SLOTS[index].split(':')[0]) + 1).toString().padStart(2, '0')}:00`;
  };

  const getMergedRangesForCourt = (courtId: number, date: string) => {
    const courtBookings = bookings.filter(b => b.courtId === courtId && b.date === date);
    const ranges: { startIdx: number; endIdx: number; booking: any }[] = [];

    courtBookings.forEach(booking => {
      const startIdx = TIME_SLOTS.indexOf(booking.startTime);
      const endIdx = TIME_SLOTS.indexOf(booking.endTime) - 1;
      if (startIdx >= 0 && endIdx >= startIdx) {
        ranges.push({
          startIdx,
          endIdx,
          booking,
        });
      }
    });

    ranges.sort((a, b) => a.startIdx - b.startIdx);
    return ranges;
  };

  const getCellStatus = (courtId: number, timeIndex: number) => {
    const ranges = getMergedRangesForCourt(courtId, selectedDate);
    for (const range of ranges) {
      if (timeIndex >= range.startIdx && timeIndex <= range.endIdx) {
        const b = range.booking;
        if (b.status === 'confirmed') return { type: 'confirmed', booking: b };
        if (b.status === 'pending') {
          if (b.userName === currentUser) return { type: 'myPending', booking: b };
          else return { type: 'otherPending', booking: b };
        }
      }
    }
    return { type: 'available', booking: null };
  };

  const getMergedRangesForSelection = () => {
    if (selectedCells.length === 0) return null;
    const grouped: { [courtId: number]: number[] } = {};
    selectedCells.forEach(({ courtId, timeIndex }) => {
      if (!grouped[courtId]) grouped[courtId] = [];
      grouped[courtId].push(timeIndex);
    });
    const result: { courtId: number; startIdx: number; endIdx: number }[] = [];
    for (const [courtIdStr, timeIndices] of Object.entries(grouped)) {
      const courtId = Number(courtIdStr);
      const sorted = timeIndices.sort((a, b) => a - b);
      let start = sorted[0];
      let end = sorted[0];
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === end + 1) {
          end = sorted[i];
        } else {
          result.push({ courtId, startIdx: start, endIdx: end });
          start = sorted[i];
          end = sorted[i];
        }
      }
      result.push({ courtId, startIdx: start, endIdx: end });
    }
    return result;
  };

  const getSelectionSummary = () => {
    const ranges = getMergedRangesForSelection();
    if (!ranges) return null;
    return ranges.map(({ courtId, startIdx, endIdx }) => {
      const courtName = courts.find(c => c.id === courtId)?.name || `Court ${courtId}`;
      const startTime = TIME_SLOTS[startIdx];
      const endTime = getSlotEndTime(endIdx);
      return `${courtName}: ${startTime} - ${endTime}`;
    });
  };

  const summary = getSelectionSummary();

  const handleCellClick = (courtId: number, timeIndex: number) => {
    const status = getCellStatus(courtId, timeIndex);
    if (status.type !== 'available') return;

    const existingIndex = selectedCells.findIndex(
      c => c.courtId === courtId && c.timeIndex === timeIndex
    );
    if (existingIndex >= 0) {
      const newSelection = selectedCells.filter((_, i) => i !== existingIndex);
      setSelectedCells(newSelection);
    } else {
      setSelectedCells([...selectedCells, { courtId, timeIndex }]);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoBase64(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmBooking = async () => {
    if (selectedCells.length === 0) return;

    const ranges = getMergedRangesForSelection();
    if (!ranges) return;

    for (const { courtId, startIdx, endIdx } of ranges) {
      const startTime = TIME_SLOTS[startIdx];
      const endTime = getSlotEndTime(endIdx);
      if (isSlotOccupied(courtId, selectedDate, startTime, endTime)) {
        alert('Some selected slots are now occupied. Please refresh and try again.');
        setSelectedCells([]);
        setShowConfirmModal(false);
        return;
      }
    }

    const date = selectedDate;
    const userNameInput = userName.trim() || 'Guest';
    setCurrentUser(userNameInput);

    for (const { courtId, startIdx, endIdx } of ranges) {
      const startTime = TIME_SLOTS[startIdx];
      const endTime = getSlotEndTime(endIdx);
      await addBooking({
        courtId,
        date,
        startTime,
        endTime,
        userName: userNameInput,
        status: 'pending',
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        address: address.trim(),
        email: email.trim(),
        photoUrl: photoBase64,
        adminNotes: '',
      });
    }

    await refreshBookings();
    setSelectedCells([]);
    setShowConfirmModal(false);
    navigate('/confirm');
  };

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const hasAvailableSlots = (date: string) => {
    for (const court of courts) {
      for (let i = 0; i < TIME_SLOTS.length; i++) {
        const start = TIME_SLOTS[i];
        const end = getSlotEndTime(i);
        if (!isSlotOccupied(court.id, date, start, end)) {
          return true;
        }
      }
    }
    return false;
  };

  return (
    <div className="max-w-6xl mx-auto p-4 pb-32">
      <h2 className="text-3xl font-bold text-green-700 mb-2 flex items-center gap-2">
        <span>🏸</span> Book a Court
      </h2>

      <div className="bg-white p-4 rounded-lg shadow-md mb-6 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[140px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Date</label>
          <input
            type="date"
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md border"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setSelectedCells([]);
            }}
            min={today}
            max={maxDateStr}
          />
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
          <input
            type="text"
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md border"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            placeholder="Enter your name"
          />
        </div>
        <button
          onClick={() => setSelectedCells([])}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 whitespace-nowrap"
        >
          Clear Selection
        </button>
        {hasAvailableSlots(selectedDate) ? (
          <span className="px-4 py-2 text-green-600 font-medium ml-auto whitespace-nowrap">
            ✅ Available courts on {selectedDate}
          </span>
        ) : (
          <span className="px-4 py-2 text-red-600 font-medium ml-auto whitespace-nowrap">
            ❌ No available slots on {selectedDate}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4 text-xs sm:text-sm bg-white p-2 sm:p-3 rounded-lg shadow-sm booking-legend">
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="inline-block w-4 h-4 bg-green-100 border border-green-300 rounded"></span>
          Available
        </span>
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="inline-block w-4 h-4 bg-green-600 rounded"></span>
          Selected
        </span>
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="inline-block w-4 h-4 bg-yellow-300 border border-yellow-500 rounded"></span>
          Your Pending
        </span>
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="inline-block w-4 h-4 bg-orange-200 border border-orange-400 rounded"></span>
          Pending (others)
        </span>
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="inline-block w-4 h-4 bg-red-200 border border-red-400 rounded"></span>
          Confirmed
        </span>
      </div>

      {summary && (
        <div className="fixed bottom-8 left-0 right-0 z-50 bg-gradient-to-r from-green-600 to-green-700 text-white p-4 shadow-2xl">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div className="flex-1 min-w-[200px]">
              <p className="font-bold text-sm uppercase tracking-wide">Selected Slots</p>
              <ul className="text-sm text-green-100 list-disc list-inside">
                {summary.map((line, idx) => (
                  <li key={idx}>{line}</li>
                ))}
              </ul>
              <p className="text-xs text-green-200 mt-1">User: {userName || 'Guest'}</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">
                {summary.length} booking{summary.length > 1 ? 's' : ''}
              </span>
              <button
                onClick={() => setShowConfirmModal(true)}
                className="px-8 py-3 bg-yellow-400 hover:bg-yellow-300 text-green-900 font-bold rounded-lg shadow-lg transition transform hover:scale-105"
              >
                Review & Confirm
              </button>
              <button
                onClick={() => setSelectedCells([])}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto bg-white rounded-lg shadow booking-grid-wrapper">
        <div className="min-w-[600px]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider rounded-tl-lg w-32 min-w-[120px]">
                  Time Slot
                </th>
                {courts.map(court => (
                  <th key={court.id} className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider w-24 min-w-[96px]">
                    {court.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {TIME_SLOTS.map((time, timeIndex) => {
                const courtCells = courts.map(court => {
                  const status = getCellStatus(court.id, timeIndex);
                  const isSelected = selectedCells.some(c => c.courtId === court.id && c.timeIndex === timeIndex);
                  const isInMergedRange = status.type !== 'available';
                  let rowSpan = 1;
                  let mergedBooking = null;
                  let shouldRender = true;

                  if (isInMergedRange) {
                    const ranges = getMergedRangesForCourt(court.id, selectedDate);
                    for (const range of ranges) {
                      if (timeIndex >= range.startIdx && timeIndex <= range.endIdx) {
                        if (timeIndex === range.startIdx) {
                          rowSpan = range.endIdx - range.startIdx + 1;
                          mergedBooking = range.booking;
                          shouldRender = true;
                        } else {
                          shouldRender = false;
                        }
                        break;
                      }
                    }
                  }

                  return {
                    court,
                    timeIndex,
                    isSelected,
                    status,
                    isInMergedRange,
                    rowSpan,
                    mergedBooking,
                    shouldRender,
                  };
                });

                return (
                  <tr key={time} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-500 whitespace-nowrap font-medium">
                      {time} - {getSlotEndTime(timeIndex)}
                    </td>
                    {courtCells.map((cell) => {
                      if (!cell.shouldRender) return null;

                      let bgColor = '';
                      let cursor = 'cursor-pointer';
                      let content = <span className="text-gray-300">○</span>;

                      if (cell.isInMergedRange) {
                        const b = cell.mergedBooking;
                        const isConfirmed = b?.status === 'confirmed';
                        const isMyPending = b?.userName === currentUser && b?.status === 'pending';
                        const isOtherPending = b?.userName !== currentUser && b?.status === 'pending';

                        if (isConfirmed) {
                          bgColor = 'bg-red-200';
                          cursor = 'cursor-not-allowed';
                          content = (
                            <div className="flex flex-col items-center">
                              <span className="text-red-600 text-sm font-medium">🔒</span>
                              <span className="text-xs text-gray-700 truncate max-w-[80px]">{b?.userName || 'Booked'}</span>
                            </div>
                          );
                        } else if (isMyPending) {
                          bgColor = 'bg-yellow-300';
                          cursor = 'cursor-not-allowed';
                          content = (
                            <div className="flex flex-col items-center">
                              <span className="text-yellow-800 text-sm font-medium">⏳</span>
                              <span className="text-xs text-gray-700 truncate max-w-[80px]">{b?.userName || 'You'}</span>
                            </div>
                          );
                        } else if (isOtherPending) {
                          bgColor = 'bg-orange-200';
                          cursor = 'cursor-not-allowed';
                          content = (
                            <div className="flex flex-col items-center">
                              <span className="text-orange-800 text-sm font-medium">⏳</span>
                              <span className="text-xs text-gray-700 truncate max-w-[80px]">{b?.userName || 'Other'}</span>
                            </div>
                          );
                        }
                      } else if (cell.isSelected) {
                        bgColor = 'bg-green-600 text-white';
                        content = <span className="text-white font-bold text-lg">✓</span>;
                      } else {
                        bgColor = 'hover:bg-green-100';
                        content = <span className="text-gray-300">○</span>;
                      }

                      const isClickable = !cell.isInMergedRange && cell.status.type === 'available';

                      return (
                        <td
                          key={`${cell.court.id}-${timeIndex}`}
                          rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
                          className={`px-2 py-2 border border-gray-200 text-center transition-all duration-200 relative ${bgColor} ${cursor} h-12 min-h-[48px]`}
                          onClick={() => {
                            if (isClickable) {
                              handleCellClick(cell.court.id, timeIndex);
                            }
                          }}
                        >
                          <div className="flex items-center justify-center w-full h-full">
                            {content}
                          </div>
                        </td>
                      );
                    }).filter(Boolean)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-4 text-center">
        Click a cell to select it. Click again to deselect. Consecutive slots on the same court are merged into one booking.
      </p>

      {showConfirmModal && summary && (
        <Modal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          title="Review & Complete Your Booking"
          size="xl"
        >
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="font-medium text-green-800">Selected Slots:</p>
              <ul className="mt-1 space-y-1 text-sm text-gray-700 list-disc list-inside">
                {summary.map((line, idx) => (
                  <li key={idx}>{line}</li>
                ))}
              </ul>
              <p className="mt-2 text-sm text-gray-600">Date: <span className="font-medium">{selectedDate}</span></p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name *</label>
                <input
                  type="text"
                  className="mt-1 block w-full border rounded px-3 py-2"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                <input
                  type="text"
                  className="mt-1 block w-full border rounded px-3 py-2"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <input
                  type="text"
                  className="mt-1 block w-full border rounded px-3 py-2"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Main St, City"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                <input
                  type="email"
                  className="mt-1 block w-full border rounded px-3 py-2"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Upload Payment Receipt (Photo)</label>
                <input
                  type="file"
                  accept="image/*"
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                  onChange={handlePhotoUpload}
                />
                {photoBase64 && (
                  <div className="mt-2">
                    <img src={photoBase64} alt="Receipt Preview" className="h-20 w-auto rounded border" />
                  </div>
                )}
              </div>
            </div>

            {settings?.adminGlobalNotes && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>📝 Admin Notes:</strong> {settings.adminGlobalNotes}
                </p>
              </div>
            )}
            {settings?.adminGlobalImage && (
              <div className="mt-2">
                <img src={settings.adminGlobalImage} alt="Admin instructions" className="max-h-32 w-auto rounded border" />
              </div>
            )}

            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                ⚠️ Once confirmed, your booking will be sent to the owner for approval.
                You will see it as pending until the owner confirms it.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmBooking}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-md"
              >
                Confirm Booking ({summary.length} booking{summary.length > 1 ? 's' : ''})
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BookingSystem;